import React, { useState, useEffect } from 'react';
import { pagesAPI } from '../../services/api';
import { 
  Plus, 
  Edit, 
  Trash2, 
  Save, 
  X, 
  Clock,
  Calendar,
  Users,
  Zap,
  Headphones,
  Globe,
  Mail,
  Bell,
  Star,
  Shield,
  Cloud,
  Code,
  Palette,
  Database,
  Smartphone,
  ShoppingCart,
  ArrowRight,
  ArrowLeft,
  CheckCircle,
  Settings,
  DollarSign,
  Download,
  Send,
  MessageCircle
} from 'lucide-react';
import { useNotificationHook } from '../../hooks/useNotificationHook';

const ComingSoonPageManagement = () => {
  const { notify } = useNotificationHook();
  const [pageData, setPageData] = useState({
    id: null, // Add ID field
    page_type: 'coming-soon', // Add page_type field
    title: 'Something Amazing is Coming',
    subtitle: 'We are working hard to bring you something incredible. Stay tuned!',
    description: 'Our team is putting the finishing touches on something special. Be the first to know when we launch.',
    content: {
      countdown: {
        days_label: 'Days',
        hours_label: 'Hours',
        minutes_label: 'Minutes',
        seconds_label: 'Seconds',
        launch_date: '2024-12-31T23:59:59'
      },
      newsletter: {
        title: 'Get Notified',
        description: 'Be the first to know when we launch!',
        button_text: 'Notify Me',
        placeholder: 'Enter your email address'
      },
      features: {
        title: 'What to Expect',
        items: [
          {
            icon: 'Users',
            title: 'Innovative Design',
            description: 'Cutting-edge user experience'
          },
          {
            icon: 'Zap',
            title: 'Powerful Features',
            description: 'Powerful functionality'
          },
          {
            icon: 'Headphones',
            title: '24/7 Support',
            description: 'Always here to help'
          }
        ]
      },
      social_links: {
        title: 'Follow Us',
        items: [
          {
            name: 'Twitter',
            url: '#',
            icon: 'twitter'
          },
          {
            name: 'Facebook',
            url: '#',
            icon: 'facebook'
          },
          {
            name: 'Instagram',
            url: '#',
            icon: 'instagram'
          }
        ]
      },
      main_icon: 'Bell' // Add main_icon to initial state
    }
  });

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  // Available icons for selection
  const availableIcons = [
    { name: 'Users', component: Users },
    { name: 'Zap', component: Zap },
    { name: 'Headphones', component: Headphones },
    { name: 'Globe', component: Globe },
    { name: 'Mail', component: Mail },
    { name: 'Bell', component: Bell },
    { name: 'Star', component: Star },
    { name: 'Shield', component: Shield },
    { name: 'Cloud', component: Cloud },
    { name: 'Code', component: Code },
    { name: 'Palette', component: Palette },
    { name: 'Database', component: Database },
    { name: 'Smartphone', component: Smartphone },
    { name: 'ShoppingCart', component: ShoppingCart },
    { name: 'ArrowRight', component: ArrowRight },
    { name: 'ArrowLeft', component: ArrowLeft },
    { name: 'CheckCircle', component: CheckCircle },
    { name: 'Settings', component: Settings },
    { name: 'DollarSign', component: DollarSign },
    { name: 'Download', component: Download },
    { name: 'Send', component: Send },
    { name: 'MessageCircle', component: MessageCircle },
    { name: 'Clock', component: Clock },
    { name: 'Calendar', component: Calendar }
  ];

  const getIconComponent = (iconName) => {
    const icon = availableIcons.find(i => i.name === iconName);
    return icon ? icon.component : Users;
  };

  const fetchPageData = async () => {
    try {
      setLoading(true);
      const response = await pagesAPI.getByType('coming-soon');
      
      if (response?.data?.success) {
        const data = response.data.data;
        setPageData({
          id: data.id, // Add the page ID
          page_type: data.page_type || pageData.page_type, // Add the page_type field
          title: data.title || pageData.title,
          subtitle: data.subtitle || pageData.subtitle,
          description: data.description || pageData.description,
          content: {
            countdown: data.content?.countdown || pageData.content.countdown,
            newsletter: data.content?.newsletter || pageData.content.newsletter,
            features: data.content?.features || pageData.content.features,
            social_links: data.content?.social_links || pageData.content.social_links,
            main_icon: data.content?.main_icon || 'Bell'
          }
        });
      }
          } catch (error) {
        console.error('Error fetching page data:', error);
        notify.error('Failed to fetch page data', 'Loading Error');
      } finally {
      setLoading(false);
    }
  };

  const savePageData = async () => {
    try {
      setSaving(true);
      
      if (!pageData.id) {
        throw new Error('Page ID not found. Please refresh the page and try again.');
      }

      const saveData = {
        page_type: pageData.page_type,
        title: pageData.title,
        subtitle: pageData.subtitle,
        description: pageData.description,
        content: pageData.content
      };

      console.log('💾 Saving page data:', saveData);
      console.log('🎯 Main icon being saved:', pageData.content?.main_icon);

      const response = await pagesAPI.update(pageData.id, saveData);

              if (response?.data?.success) {
          notify.success('Page data saved successfully!', 'Success');
        } else {
          throw new Error('Failed to save page data');
        }
      } catch (error) {
        console.error('Error saving page data:', error);
        notify.error(error.message || 'Failed to save page data. Please try again.', 'Save Error');
    } finally {
      setSaving(false);
    }
  };

  const handleContentChange = (section, field, value) => {
    setPageData(prev => ({
      ...prev,
      content: {
        ...prev.content,
        [section]: {
          ...prev.content[section],
          [field]: value
        }
      }
    }));
  };

  const handleMainIconChange = (value) => {
    console.log('🔍 Main icon changed to:', value);
    setPageData(prev => {
      const newData = {
        ...prev,
        content: {
          ...prev.content,
          main_icon: value
        }
      };
      console.log('📝 Updated pageData:', newData);
      return newData;
    });
  };

  const handleFeatureChange = (index, field, value) => {
    setPageData(prev => ({
      ...prev,
      content: {
        ...prev.content,
        features: {
          ...prev.content.features,
          items: prev.content.features.items.map((item, i) => 
            i === index ? { ...item, [field]: value } : item
          )
        }
      }
    }));
  };

  const addFeature = () => {
    setPageData(prev => ({
      ...prev,
      content: {
        ...prev.content,
        features: {
          ...prev.content.features,
          items: [...prev.content.features.items, {
            icon: 'Users',
            title: 'New Feature',
            description: 'Feature description'
          }]
        }
      }
    }));
  };

  const removeFeature = (index) => {
    if (window.confirm('Are you sure you want to delete this feature? This action cannot be undone.')) {
      setPageData(prev => ({
        ...prev,
        content: {
          ...prev.content,
          features: {
            ...prev.content.features,
            items: prev.content.features.items.filter((_, i) => i !== index)
          }
        }
      }));
    }
  };

  const handleSocialLinkChange = (index, field, value) => {
    setPageData(prev => ({
      ...prev,
      content: {
        ...prev.content,
        social_links: {
          ...prev.content.social_links,
          items: prev.content.social_links.items.map((item, i) => 
            i === index ? { ...item, [field]: value } : item
          )
        }
      }
    }));
  };

  const addSocialLink = () => {
    setPageData(prev => ({
      ...prev,
      content: {
        ...prev.content,
        social_links: {
          ...prev.content.social_links,
          items: [...prev.content.social_links.items, {
            name: 'New Social',
            url: '#',
            icon: 'Globe'
          }]
        }
      }
    }));
  };

  const removeSocialLink = (index) => {
    if (window.confirm('Are you sure you want to delete this social link? This action cannot be undone.')) {
      setPageData(prev => ({
        ...prev,
        content: {
          ...prev.content,
          social_links: {
            ...prev.content.social_links,
            items: prev.content.social_links.items.filter((_, i) => i !== index)
          }
        }
      }));
    }
  };

  const clearMessage = () => {
    setMessage({ type: '', text: '' });
  };

  useEffect(() => {
    fetchPageData();
  }, []);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">
          Coming Soon Page - Content Management
        </h1>
        <p className="text-gray-600 dark:text-gray-400 mt-1">
          Manage the content sections of the Coming Soon page
        </p>
      </div>

      {/* Message */}
      {message.text && (
        <div className={`p-4 rounded-lg mb-6 ${
          message.type === 'success' 
            ? 'bg-green-100 text-green-700 border border-green-200' 
            : 'bg-red-100 text-red-700 border border-red-200'
        }`}>
          <div className="flex justify-between items-center">
            <span>{message.text}</span>
            <button onClick={clearMessage} className="text-gray-500 hover:text-gray-700">
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      <div className="space-y-8">
        {/* Main Header Section */}
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4 sm:p-6">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            Main Header Section
          </h2>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Main Icon
              </label>
              <select
                value={pageData.content?.main_icon || 'Bell'}
                onChange={(e) => handleMainIconChange(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
              >
                {availableIcons.map(icon => (
                  <option key={icon.name} value={icon.name}>
                    {icon.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Main Title
              </label>
              <input
                type="text"
                value={pageData.title}
                onChange={(e) => setPageData(prev => ({ ...prev, title: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                placeholder="Something Amazing is Coming"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Subtitle
              </label>
              <input
                type="text"
                value={pageData.subtitle}
                onChange={(e) => setPageData(prev => ({ ...prev, subtitle: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                placeholder="We are working hard to bring you something incredible. Stay tuned!"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Description
              </label>
              <textarea
                value={pageData.description}
                onChange={(e) => setPageData(prev => ({ ...prev, description: e.target.value }))}
                rows="3"
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                placeholder="Our team is putting the finishing touches on something special. Be the first to know when we launch."
              />
            </div>
          </div>
        </div>

        {/* Countdown Timer Section */}
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4 sm:p-6">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            Countdown Timer Section
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Days Label
              </label>
              <input
                type="text"
                value={pageData.content.countdown.days_label}
                onChange={(e) => handleContentChange('countdown', 'days_label', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Hours Label
              </label>
              <input
                type="text"
                value={pageData.content.countdown.hours_label}
                onChange={(e) => handleContentChange('countdown', 'hours_label', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Minutes Label
              </label>
              <input
                type="text"
                value={pageData.content.countdown.minutes_label}
                onChange={(e) => handleContentChange('countdown', 'minutes_label', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Seconds Label
              </label>
              <input
                type="text"
                value={pageData.content.countdown.seconds_label}
                onChange={(e) => handleContentChange('countdown', 'seconds_label', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Launch Date
              </label>
              <input
                type="datetime-local"
                value={pageData.content.countdown.launch_date.replace('Z', '')}
                onChange={(e) => handleContentChange('countdown', 'launch_date', e.target.value + 'Z')}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
              />
            </div>
          </div>
        </div>

        {/* Newsletter Section */}
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4 sm:p-6">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            Newsletter Section
          </h2>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Title
              </label>
              <input
                type="text"
                value={pageData.content.newsletter.title}
                onChange={(e) => handleContentChange('newsletter', 'title', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Description
              </label>
              <textarea
                value={pageData.content.newsletter.description}
                onChange={(e) => handleContentChange('newsletter', 'description', e.target.value)}
                rows="3"
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Button Text
                </label>
                <input
                  type="text"
                  value={pageData.content.newsletter.button_text}
                  onChange={(e) => handleContentChange('newsletter', 'button_text', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Placeholder Text
                </label>
                <input
                  type="text"
                  value={pageData.content.newsletter.placeholder}
                  onChange={(e) => handleContentChange('newsletter', 'placeholder', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Features Section */}
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4 sm:p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
              What to Expect Section
            </h2>
            <button
              onClick={addFeature}
              className="bg-primary-600 hover:bg-primary-700 text-white px-3 py-1 rounded-lg flex items-center gap-2 text-sm transition-colors"
            >
              <Plus className="w-4 h-4" />
              Add Feature
            </button>
          </div>
          
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Section Title
            </label>
            <input
              type="text"
              value={pageData.content.features.title}
              onChange={(e) => handleContentChange('features', 'title', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
            />
          </div>

          <div className="space-y-4">
            {pageData.content.features.items.map((feature, index) => (
              <div key={index} className="border border-gray-200 dark:border-gray-600 rounded-lg p-4">
                <div className="flex justify-between items-start mb-4">
                  <h3 className="text-md font-medium text-gray-900 dark:text-white">
                    Feature {index + 1}
                  </h3>
                  <button
                    onClick={() => removeFeature(index)}
                    className="text-red-600 hover:text-red-700"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Icon
                    </label>
                    <select
                      value={feature.icon}
                      onChange={(e) => handleFeatureChange(index, 'icon', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                    >
                      {availableIcons.map(icon => (
                        <option key={icon.name} value={icon.name}>
                          {icon.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Title
                    </label>
                    <input
                      type="text"
                      value={feature.title}
                      onChange={(e) => handleFeatureChange(index, 'title', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Description
                    </label>
                    <input
                      type="text"
                      value={feature.description}
                      onChange={(e) => handleFeatureChange(index, 'description', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Social Links Section */}
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4 sm:p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
              Follow Us Section
            </h2>
            <button
              onClick={addSocialLink}
              className="bg-primary-600 hover:bg-primary-700 text-white px-3 py-1 rounded-lg flex items-center gap-2 text-sm transition-colors"
            >
              <Plus className="w-4 h-4" />
              Add Social Link
            </button>
          </div>
          
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Section Title
            </label>
            <input
              type="text"
              value={pageData.content.social_links.title}
              onChange={(e) => handleContentChange('social_links', 'title', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
            />
          </div>

          <div className="space-y-4">
            {pageData.content.social_links.items.map((social, index) => (
              <div key={index} className="border border-gray-200 dark:border-gray-600 rounded-lg p-4">
                <div className="flex justify-between items-start mb-4">
                  <h3 className="text-md font-medium text-gray-900 dark:text-white">
                    Social Link {index + 1}
                  </h3>
                  <button
                    onClick={() => removeSocialLink(index)}
                    className="text-red-600 hover:text-red-700"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Name
                    </label>
                    <input
                      type="text"
                      value={social.name}
                      onChange={(e) => handleSocialLinkChange(index, 'name', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      URL
                    </label>
                    <input
                      type="url"
                      value={social.url}
                      onChange={(e) => handleSocialLinkChange(index, 'url', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Icon
                    </label>
                    <select
                      value={social.icon}
                      onChange={(e) => handleSocialLinkChange(index, 'icon', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                    >
                      {availableIcons.map(icon => (
                        <option key={icon.name} value={icon.name}>
                          {icon.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Save Button */}
      <div className="mt-8 flex justify-end">
        <button
          onClick={savePageData}
          disabled={saving}
          className="bg-primary-600 hover:bg-primary-700 text-white px-6 py-3 rounded-lg flex items-center gap-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {saving ? (
            <>
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
              Saving...
            </>
          ) : (
            <>
              <Save className="w-5 h-5" />
              Save Changes
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default ComingSoonPageManagement; 